import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { Play, Volume2, VolumeX } from 'lucide-react';

import s from './VideoPreview.css';

/**
 * Extrae el ID de YouTube de cualquier formato de URL
 * Soporta: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID, youtube.com/shorts/ID
 */
function extractYouTubeId(url) {
  if (!url) return null;
  
  // Si ya es solo el ID (11 caracteres alfanuméricos con guiones/underscores)
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) return url;
  
  // Patrones para diferentes formatos de URL de YouTube
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,           // youtube.com/watch?v=ID
    /(?:youtube\.com\/watch\?.*v=)([a-zA-Z0-9_-]{11})/,         // youtube.com/watch?other=x&v=ID
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,                       // youtu.be/ID
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,             // youtube.com/embed/ID
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,            // youtube.com/shorts/ID
    /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,                 // youtube.com/v/ID (legacy)
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
}

class VideoPreview extends React.Component {
  static propTypes = {
    youtubeUrl: PropTypes.string,
    thumbnailUrl: PropTypes.string.isRequired,
    alt: PropTypes.string,
    className: PropTypes.string,
    isOverlay: PropTypes.bool,
    isHovering: PropTypes.bool, // Controlado externamente para modo overlay
    isDetailPage: PropTypes.bool, // Para la página de detalle con controles de sonido
  };

  static defaultProps = {
    youtubeUrl: null,
    alt: 'Preview',
    className: '',
    isOverlay: false,
    isHovering: false,
    isDetailPage: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      isHovering: false,
      showVideo: false,
      isMuted: true,
    };
    this.hoverTimeoutRef = null;
    this.iframeRef = React.createRef();
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.toggleMute = this.toggleMute.bind(this);
  }

  toggleMute(e) {
    e.stopPropagation();
    this.setState(prevState => ({ isMuted: !prevState.isMuted }));
  }

  componentWillUnmount() {
    if (this.hoverTimeoutRef) {
      clearTimeout(this.hoverTimeoutRef);
    }
  }

  componentDidUpdate(prevProps) {
    const { isOverlay, isHovering } = this.props;
    
    // Para modo overlay, manejar hover externo
    if (isOverlay) {
      if (isHovering && !prevProps.isHovering) {
        // Empezó hover externo
        this.hoverTimeoutRef = setTimeout(() => {
          this.setState({ showVideo: true });
        }, 500);
      } else if (!isHovering && prevProps.isHovering) {
        // Terminó hover externo
        if (this.hoverTimeoutRef) {
          clearTimeout(this.hoverTimeoutRef);
          this.hoverTimeoutRef = null;
        }
        this.setState({ showVideo: false });
      }
    }
  }

  handleMouseEnter() {
    const { youtubeUrl } = this.props;
    const videoId = extractYouTubeId(youtubeUrl);
    
    if (!videoId) return;
    
    this.setState({ isHovering: true });
    
    // Delay de 500ms antes de mostrar el video
    this.hoverTimeoutRef = setTimeout(() => {
      this.setState({ showVideo: true });
    }, 500);
  }

  handleMouseLeave() {
    this.setState({ 
      isHovering: false,
      showVideo: false 
    });
    
    if (this.hoverTimeoutRef) {
      clearTimeout(this.hoverTimeoutRef);
      this.hoverTimeoutRef = null;
    }
  }

  render() {
    const { youtubeUrl, thumbnailUrl, alt, className, isOverlay, isDetailPage } = this.props;
    const { showVideo, isMuted } = this.state;
    
    const videoId = extractYouTubeId(youtubeUrl);
    
    // URL del embed de YouTube - usando nocookie para máxima privacidad y sin branding
    const muteParam = isMuted ? '1' : '0';
    // En página de detalle usar HD720 para mejor calidad, en tarjetas usar calidad automática
    const qualityParam = isDetailPage ? '&vq=hd720' : '';
    const embedUrl = videoId 
      ? `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=${muteParam}&controls=0&modestbranding=1&rel=0&showinfo=0&loop=1&playlist=${videoId}&playsinline=1&iv_load_policy=3&disablekb=1&fs=0&cc_load_policy=0&start=1${qualityParam}`
      : null;

    // Modo overlay: controlado externamente por prop isHovering
    // No bloquea otros elementos (flechas, wishlist)
    if (isOverlay) {
      const { isHovering: externalHovering } = this.props;
      const shouldShowVideo = externalHovering && showVideo;
      
      // Clase CSS diferente para página de detalle
      const wrapperClass = isDetailPage ? s.videoWrapperDetail : s.videoWrapperOverlay;
      
      return (
        <>
          {/* Indicador de video disponible - posición absoluta, no bloquea nada */}
          {videoId && !shouldShowVideo && (
            <div className={isDetailPage ? s.videoIndicatorDetail : s.videoIndicatorOverlay}>
              <Play size={isDetailPage ? 24 : 14} fill="currentColor" />
            </div>
          )}
          
          {/* Video solo se muestra cuando hay hover externo + delay cumplido */}
          {shouldShowVideo && videoId && (
            <div className={wrapperClass}>
              <iframe
                key={isMuted ? 'muted' : 'unmuted'}
                ref={this.iframeRef}
                src={embedUrl}
                className={s.video}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={alt}
              />
              {/* Botón de mute/unmute solo en página de detalle */}
              {isDetailPage && (
                <button 
                  className={s.muteButton}
                  onClick={this.toggleMute}
                  title={isMuted ? 'Activar sonido' : 'Silenciar'}
                >
                  {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </button>
              )}
            </div>
          )}
        </>
      );
    }

    // Modo normal: muestra thumbnail + video
    return (
      <div 
        className={`${s.container} ${className}`}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        {/* Thumbnail (siempre visible inicialmente) */}
        <img 
          src={thumbnailUrl}
          alt={alt}
          className={`${s.thumbnail} ${showVideo ? s.thumbnailHidden : ''}`}
          loading="lazy"
        />
        
        {/* Video (solo se carga en hover después del delay) */}
        {showVideo && videoId && (
          <div className={s.videoWrapper}>
            <iframe
              src={embedUrl}
              className={s.video}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={alt}
            />
            {/* Overlay transparente para evitar interacción con el iframe */}
            <div className={s.videoOverlay} />
          </div>
        )}
        
        {/* Indicador de video disponible (icono de Play) */}
        {videoId && !showVideo && (
          <div className={s.videoIndicator}>
            <Play size={16} fill="currentColor" />
          </div>
        )}
      </div>
    );
  }
}

// Exportar también la función de extracción para uso en formularios
export { extractYouTubeId };

export default withStyles(s)(VideoPreview);
